import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import blobStorageService from '../utils/blobStorage';
import { validateFile, getFileMetadata } from '../utils/fileUtils';

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const DropzoneActive = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  borderRadius: theme.shape.borderRadius,
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

// Helper to add alpha to color
function alpha(color, value) {
  return color + parseInt(value * 255).toString(16).padStart(2, '0');
}

const FileUploader = ({ onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    // Reset states
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    
    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];
    
    // Validate file
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    // Set file and metadata
    setFile(file);
    setFileMetadata(getFileMetadata(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 5;
          if (newProgress >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return newProgress;
        });
      }, 200);

      // Upload file to Vercel Blob Storage
      console.log('🚀 Starting file upload to Vercel Blob Storage');
      const result = await blobStorageService.uploadFile(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      console.log('✅ Upload completed successfully:', result);
      setSuccess('File uploaded successfully to Vercel Blob Storage!');

      // Call the onUploadComplete callback with the result
      if (onUploadComplete) {
        onUploadComplete({
          url: result.url || result.blobUrl,
          path: result.path || result.fileHash,
          metadata: fileMetadata,
          originalFilename: result.originalFilename,
          hashFilename: result.hashFilename,
          ipfsGatewayUrl: result.url || result.blobUrl, // For backward compatibility
          fileHash: result.fileHash,
          blobUrl: result.blobUrl
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setFileMetadata(null);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
  };

  return (
    <Box sx={{ my: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ position: 'relative' }}>
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          
          {isDragActive && (
            <DropzoneActive>
              <Typography variant="h6" color="primary">
                Drop the file here...
              </Typography>
            </DropzoneActive>
          )}
          
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          
          <Typography variant="h6" gutterBottom>
            {file ? 'File ready for upload' : 'Drag & drop a file here, or click to select'}
          </Typography>
          
          {file ? (
            <Typography variant="body2" color="text.secondary">
              Selected file: <strong>{file.name}</strong> ({fileMetadata?.size})
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Supports: images, videos, audio, documents, code/text, 3D models (.glb, .gltf, .obj, .stl, .vox), archives, and more
            </Typography>
          )}
        </DropzoneContainer>
      </Box>

      {file && (
        <Box sx={{ mt: 2 }}>
          {uploading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFile}
              disabled={uploading}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : null}
            >
              {uploading ? 'Uploading...' : 'Upload to Blob Storage'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FileUploader;